import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { ApiError } from "../api/errors";

export interface ToastItem {
  id: number;
  kind: "error" | "success" | "info";
  title: string;
  detail?: string;
}

interface ToastApi {
  push: (toast: Omit<ToastItem, "id">) => void;
  pushError: (err: unknown, fallbackTitle?: string) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (toast: Omit<ToastItem, "id">) => {
      seq.current += 1;
      const id = seq.current;
      setToasts((cur) => [...cur, { ...toast, id }]);
      setTimeout(() => dismiss(id), 7000);
    },
    [dismiss],
  );

  const pushError = useCallback(
    (err: unknown, fallbackTitle = "Something went wrong") => {
      if (err instanceof ApiError) {
        push({ kind: "error", title: err.problem.title || fallbackTitle, detail: err.problem.detail });
      } else if (err instanceof Error) {
        push({ kind: "error", title: fallbackTitle, detail: err.message });
      } else {
        push({ kind: "error", title: fallbackTitle });
      }
    },
    [push],
  );

  const api = useMemo(() => ({ push, pushError, dismiss }), [push, pushError, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-container" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind === "error" ? "error" : ""}`} role="status">
            <div className="toast-title">{t.title}</div>
            {t.detail && <div>{t.detail}</div>}
            <button className="link-button" onClick={() => dismiss(t.id)} aria-label="Dismiss">
              Dismiss
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}
